import React, { useEffect, useState, useRef } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  useDisclosure, Badge, Flex, Text, Box, HStack, Select, Input, useToast,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay,
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import { FiFileText } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { alumbradoApi, ReporteAlumbrado, abrirPdfEnNuevaPestana } from '../../../api/serviciosApi';
import FormularioAlumbrado from './FormularioAlumbrado';
import ModalAlumbradoEvidencias from './ModalAlumbradoEvidencias';

const ESTATUS_COLORS: Record<string, string> = {
  Pendiente: 'yellow',
  'En proceso': 'orange',
  Atendido: 'green',
  Cancelado: 'red',
};

const ORIGEN_COLORS: Record<string, string> = {
  WhatsApp: 'green',
  Oficio: 'purple',
  Manual: 'gray',
  PortalCiudadano: 'blue',
};

const ORIGENES = ['WhatsApp', 'Manual', 'Oficio', 'PortalCiudadano'];
const ESTATUS = ['Pendiente', 'En proceso', 'Atendido', 'Cancelado'];

const TablaAlumbrado: React.FC = () => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);

  const { isOpen: isOpenVer, onOpen: onOpenVer, onClose: onCloseVer } = useDisclosure();
  const { isOpen: isOpenEditar, onOpen: onOpenEditar, onClose: onCloseEditar } = useDisclosure();
  const { isOpen: isOpenNuevo, onOpen: onOpenNuevo, onClose: onCloseNuevo } = useDisclosure();
  const { isOpen: isOpenEvidencias, onOpen: onOpenEvidencias, onClose: onCloseEvidencias } = useDisclosure();
  const { isOpen: isOpenEliminar, onOpen: onOpenEliminar, onClose: onCloseEliminar } = useDisclosure();

  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [data, setData] = useState<ReporteAlumbrado[]>([]);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const [filtroOrigen, setFiltroOrigen] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchData = async () => {
    setCargando(true);
    try {
      const res = await alumbradoApi.list();
      setData(res || []);
    } catch {
      setData([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const datosFiltrados = data.filter((item) => {
    const matchEstatus = filtroEstatus ? item.estatus === filtroEstatus : true;
    const matchOrigen = filtroOrigen ? item.origenReporte === filtroOrigen : true;
    const matchBusqueda = busqueda
      ? item.folio?.toLowerCase().includes(busqueda.toLowerCase()) ||
        item.ciudadanoNombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
        item.ciudadanoApellidos?.toLowerCase().includes(busqueda.toLowerCase()) ||
        item.tipo?.toLowerCase().includes(busqueda.toLowerCase()) ||
        item.localidad?.toLowerCase().includes(busqueda.toLowerCase())
      : true;
    return matchEstatus && matchOrigen && matchBusqueda;
  });

  const totalPages = Math.ceil(datosFiltrados.length / itemsPerPage);
  const indexUltimo = currentPage * itemsPerPage;
  const indexPrimero = indexUltimo - itemsPerPage;
  const itemsPagina = datosFiltrados.slice(indexPrimero, indexUltimo);

  const cargarDetalle = async (item: any, abrir: () => void) => {
    try {
      const res = await alumbradoApi.get(item.id);
      setSelectedItem(res);
    } catch {
      setSelectedItem(item);
    }
    abrir();
  };

  const handleGuardado = () => {
    onCloseNuevo();
    onCloseEditar();
    fetchData();
  };

  const handleEvidenciasGuardadas = () => {
    fetchData();
  };

  const handleEliminarClick = (item: any) => { setSelectedItem(item); onOpenEliminar(); };

  const handleEliminarConfirmar = async () => {
    if (!selectedItem) return;
    setProcesando(true);
    try {
      await alumbradoApi.remove(selectedItem.id);
      toast({ title: 'Reporte eliminado', status: 'success', duration: 3000 });
      onCloseEliminar();
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo eliminar.', status: 'error', duration: 4000 });
    } finally {
      setProcesando(false);
    }
  };

  const handleExportExcel = () => {
    const filas = datosFiltrados.map((item) => ({
      'Folio': item.folio,
      'Fecha': item.fecha ? new Date(item.fecha).toLocaleDateString('es-MX') : '',
      'Origen': item.origenReporte,
      'Ciudadano': `${item.ciudadanoNombre || ''} ${item.ciudadanoApellidos || ''}`.trim(),
      'Teléfono': item.ciudadanoTelefono || item.ciudadanoCelular || '',
      'Localidad': item.localidad,
      'Colonia': item.colonia,
      'Tipo': item.tipo,
      'Responsable': item.responsable,
      'Estatus': item.estatus,
    }));
    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Alumbrado');
    XLSX.writeFile(wb, `Reportes_Alumbrado_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const [generandoPdf, setGenerandoPdf] = useState(false);
  const handleReportePdf = async () => {
    setGenerandoPdf(true);
    try {
      const params: any = {};
      if (filtroEstatus) params.estatus = filtroEstatus;
      if (filtroOrigen) params.origenReporte = filtroOrigen;
      const blob = await alumbradoApi.reportePdf(params);
      abrirPdfEnNuevaPestana(blob);
    } catch {
      toast({ title: 'Error', description: 'No se pudo generar el reporte PDF.', status: 'error', duration: 4000 });
    } finally {
      setGenerandoPdf(false);
    }
  };

  const fmt = (v?: string) =>
    v ? new Date(v).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

  return (
    <Box>
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={3} flexWrap="wrap">
          <Input
            placeholder="Buscar por folio, ciudadano, tipo o localidad..."
            value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setCurrentPage(1); }}
            size="sm"
            w={{ base: 'full', md: '260px' }}
          />
          <Select placeholder="Filtrar por origen" value={filtroOrigen}
            onChange={(e) => { setFiltroOrigen(e.target.value); setCurrentPage(1); }} size="sm" w="170px">
            {ORIGENES.map((o) => <option key={o} value={o}>{o}</option>)}
          </Select>
          <Select placeholder="Filtrar por estatus" value={filtroEstatus}
            onChange={(e) => { setFiltroEstatus(e.target.value); setCurrentPage(1); }} size="sm" w="160px">
            {ESTATUS.map((e) => <option key={e} value={e}>{e}</option>)}
          </Select>
        </HStack>
        <HStack spacing={2}>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={handleExportExcel}>
            Excel
          </Button>
          <Button size="sm" leftIcon={<FiFileText />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleReportePdf} isLoading={generandoPdf}>
            Reporte PDF
          </Button>
          <Button size="sm" leftIcon={<AddIcon />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={onOpenNuevo}>
            Nuevo reporte
          </Button>
        </HStack>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg="#5b182b">
            <Tr>
              <Th color="white" fontSize="xs">Folio</Th>
              <Th color="white" fontSize="xs">Fecha</Th>
              <Th color="white" fontSize="xs">Origen</Th>
              <Th color="white" fontSize="xs">Ciudadano</Th>
              <Th color="white" fontSize="xs">Localidad</Th>
              <Th color="white" fontSize="xs">Tipo</Th>
              <Th color="white" fontSize="xs">Estatus</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={8} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : itemsPagina.length === 0 ? (
              <Tr><Td colSpan={8} textAlign="center" py={8}><Text color="gray.400">No se encontraron reportes</Text></Td></Tr>
            ) : (
              itemsPagina.map((item, idx) => (
                <Tr key={item.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                  <Td fontSize="sm">{item.folio || '—'}</Td>
                  <Td>{fmt(item.fecha)}</Td>
                  <Td>
                    <Badge colorScheme={ORIGEN_COLORS[item.origenReporte] || 'gray'} fontSize="2xs">
                      {item.origenReporte}
                    </Badge>
                  </Td>
                  <Td maxW="180px">
                    <Text noOfLines={1} fontSize="sm">
                      {`${item.ciudadanoNombre || ''} ${item.ciudadanoApellidos || ''}`.trim() || '—'}
                    </Text>
                  </Td>
                  <Td>{item.localidad || '—'}</Td>
                  <Td maxW="150px"><Text noOfLines={1} fontSize="sm">{item.tipo || '—'}</Text></Td>
                  <Td>
                    <Badge colorScheme={ESTATUS_COLORS[item.estatus] || 'gray'} fontSize="xs" px={2} py={0.5} borderRadius="full">
                      {item.estatus}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={1} flexWrap="wrap">
                      <Button size="xs" colorScheme="blue" variant="outline" onClick={() => cargarDetalle(item, onOpenVer)}>Ver</Button>
                      <Button size="xs" colorScheme="orange" variant="outline" onClick={() => cargarDetalle(item, onOpenEditar)}>Editar</Button>
                      <Button size="xs" colorScheme="teal" variant="outline" onClick={() => cargarDetalle(item, onOpenEvidencias)}>Evidencias</Button>
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => handleEliminarClick(item)}>Eliminar</Button>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {totalPages > 1 && (
        <Flex mt={4} justify="space-between" align="center">
          <Text fontSize="sm" color="gray.500">
            Mostrando {indexPrimero + 1}–{Math.min(indexUltimo, datosFiltrados.length)} de {datosFiltrados.length}
          </Text>
          <HStack spacing={1}>
            <Button size="xs" onClick={() => setCurrentPage(1)} isDisabled={currentPage === 1}>«</Button>
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} isDisabled={currentPage === 1}>‹</Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const page = Math.max(1, currentPage - 2) + i;
              if (page > totalPages) return null;
              return (
                <Button key={page} size="xs" bg={currentPage === page ? '#5b182b' : undefined} color={currentPage === page ? 'white' : undefined} onClick={() => setCurrentPage(page)}>
                  {page}
                </Button>
              );
            })}
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} isDisabled={currentPage === totalPages}>›</Button>
            <Button size="xs" onClick={() => setCurrentPage(totalPages)} isDisabled={currentPage === totalPages}>»</Button>
          </HStack>
        </Flex>
      )}

      {/* Modal: Ver */}
      <Modal isOpen={isOpenVer} onClose={onCloseVer} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">Detalle del Reporte de Alumbrado</ModalHeader>
          <ModalBody py={5}>
            {selectedItem && <FormularioAlumbrado modo="view" data={selectedItem} />}
          </ModalBody>
          <ModalFooter gap={2}><Button onClick={onCloseVer}>Cerrar</Button></ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal: Editar */}
      <Modal isOpen={isOpenEditar} onClose={onCloseEditar} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">Editar Reporte de Alumbrado</ModalHeader>
          <ModalBody py={5}>
            {selectedItem && <FormularioAlumbrado modo="edit" data={selectedItem} onGuardado={handleGuardado} onCancelar={onCloseEditar} />}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Modal: Nuevo */}
      <Modal isOpen={isOpenNuevo} onClose={onCloseNuevo} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">Nuevo Reporte de Alumbrado</ModalHeader>
          <ModalBody py={5}>
            <FormularioAlumbrado modo="new" onGuardado={handleGuardado} onCancelar={onCloseNuevo} />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Modal: Evidencias */}
      {selectedItem && (
        <ModalAlumbradoEvidencias
          isOpen={isOpenEvidencias}
          onClose={onCloseEvidencias}
          reporteId={selectedItem.id}
          titulo={selectedItem.folio || `Reporte #${selectedItem.id}`}
          onGuardado={handleEvidenciasGuardadas}
        />
      )}

      {/* AlertDialog: Eliminar */}
      <AlertDialog isOpen={isOpenEliminar} leastDestructiveRef={cancelRef} onClose={onCloseEliminar}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="#5b182b">Eliminar reporte</AlertDialogHeader>
            <AlertDialogBody>
              ¿Eliminar el reporte <strong>{selectedItem?.folio || `#${selectedItem?.id}`}</strong>? Se eliminarán los archivos asociados. Esta acción no se puede deshacer.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseEliminar} isDisabled={procesando}>Cancelar</Button>
              <Button colorScheme="red" onClick={handleEliminarConfirmar} ml={3} isLoading={procesando} loadingText="Eliminando...">Eliminar</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TablaAlumbrado;
